import React from 'react';
import { FlaskConical, ChevronRight, Clock, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import type { SampleItem } from './types';

interface SamplingTrialsWidgetProps {
  samples: SampleItem[];
  onNewSample: () => void;
}

export const SamplingTrialsWidget: React.FC<SamplingTrialsWidgetProps> = ({ samples, onNewSample }) => {
  const navigate = useNavigate();

  const sentCount = samples.filter((s) => s.status === 'sent').length;
  const feedbackCount = samples.filter((s) => s.status === 'awaiting_feedback').length;
  const passedCount = samples.filter((s) => s.status === 'successful').length;
  const convertedCount = samples.filter((s) => s.status === 'converted').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-info/10 text-info-text border-info/30';
      case 'awaiting_feedback':
        return 'bg-warning/10 text-warning-text border-warning/30';
      case 'successful':
        return 'bg-success/10 text-success-text border-success/30';
      case 'converted':
        return 'bg-primary-container text-primary border-primary/30';
      default:
        return 'bg-surface-variant text-on-surface-variant border-border';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-warning/10 text-warning-text">
              <FlaskConical className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                My Samples & Trials
              </span>
              <div className="text-data-xs text-on-surface-subtle">Product trial conversion pipeline</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onNewSample}
              className="inline-flex items-center gap-1 rounded-lg border border-warning/30 bg-warning/5 px-2.5 py-1 text-body-sm font-semibold text-warning-text hover:bg-warning/10 active:scale-95 transition"
            >
              <Plus className="h-3 w-3" />
              <span>Sample</span>
            </button>
            <button
              type="button"
              onClick={() => navigate(ROUTES.sampling)}
              className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
            >
              <span>View All</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Counter Pills */}
        <div className="my-3 grid grid-cols-4 gap-2">
          <div className="rounded-lg border border-border bg-surface-variant/70 p-2 text-center">
            <span className="text-data-xs font-medium text-on-surface-variant">Sent</span>
            <div className="font-semibold text-body-sm text-on-surface">{sentCount}</div>
          </div>
          <div className="rounded-lg border border-warning/30 bg-warning/10 p-2 text-center">
            <span className="text-data-xs font-medium text-warning-text">Feedback</span>
            <div className="font-semibold text-body-sm text-warning-text">{feedbackCount}</div>
          </div>
          <div className="rounded-lg border border-success/30 bg-success/10 p-2 text-center">
            <span className="text-data-xs font-medium text-success-text">Passed</span>
            <div className="font-semibold text-body-sm text-success-text">{passedCount}</div>
          </div>
          <div className="rounded-lg border border-primary/30 bg-primary-container/50 p-2 text-center">
            <span className="text-data-xs font-medium text-primary">Converted</span>
            <div className="font-semibold text-body-sm text-primary">{convertedCount}</div>
          </div>
        </div>

        {/* Active Samples Table */}
        {samples.length === 0 ? (
          <div className="my-6 text-center text-body-sm text-on-surface-subtle">No samples in trial right now.</div>
        ) : (
          <div className="my-2 flex flex-col divide-y divide-border">
            {samples.map((s) => (
              <div
                key={s.id}
                onClick={() => navigate(ROUTES.sampling)}
                className="group flex items-center justify-between py-2.5 transition hover:bg-surface-variant px-2 rounded-lg cursor-pointer"
              >
                <div>
                  <span className="font-semibold text-body-sm text-on-surface">{s.client}</span>
                  <div className="text-data-xs font-medium text-on-surface-variant">{s.product}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-data-xs text-on-surface-variant">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3 text-on-surface-subtle" />
                      Feedback due: {s.feedbackDueDate || '—'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-md border px-2 py-0.5 text-data-xs font-semibold capitalize ${getStatusBadge(
                      s.status
                    )}`}
                  >
                    {s.statusLabel}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Converted to orders:</span>
        <span className="font-semibold text-success-text">
          {convertedCount} of {samples.length} sample{samples.length === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
};

